import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';
import { Reorder } from 'motion/react';

import { PROD_MEDIA_SERVER } from '@osmosis/constants';
import { formatSecondsToDuration, nameToLearnUrl } from '@osmosis/shared/video';
import { getTranslatedTitle } from '../../../lib/shared/playlist/utils';
import { useUserContext } from '../../../context/UserContext';
import { useLanguageContext } from '../../../context/LanguageProvider';

import { A } from '@osmosis/tailwind-ui/components/Text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { Tag } from '@osmosis/tailwind-ui/components/Tag';

import ItemThumbnail from '../../../components/video/ItemThumbnail';
import {
  textGray780,
  textNegativeText,
} from '@osmosis/tailwind-ui/styles/text';
import PopupMenu from '../../../components/PopupMenu';
import PopupMenuRow from '../../../components/PopupMenuRow';
import LockIcon from '../../../components/search/LockIcon';

import { faUpLong } from '@fortawesome/pro-regular-svg-icons/faUpLong';
import { faDownLong } from '@fortawesome/pro-regular-svg-icons/faDownLong';
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons/faEllipsisV';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';

import { textGray, textPrimary } from '@osmosis/tailwind-ui/styles/text';
import { bg200, bgPrimaryLight, bg0 } from '@osmosis/tailwind-ui/styles';

import {
  PlaylistItemProps,
  PlaylistLearnNodeItem,
} from '@osmosis/shared/types/playlist';

import useModal from '../../../hooks/useModal';

type Props = {
  playlistItem: PlaylistItemProps;
  onRemove: (items: PlaylistLearnNodeItem[]) => void;
  isPlaylistMine: boolean;
  playlistUrl: string;
  moveItemAmount: (nodeID: number, moveAmount: number) => void;
  rowNumber: number;
  maxRows: number;
  modalOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
  retiredFeatureFlag: boolean;
  searchToggle: () => void;
};

/**
 * Displays video item info.
 */
const PlaylistItemRow = (props: Props) => {
  const {
    playlistItem,
    onRemove,
    isPlaylistMine,
    playlistUrl,
    moveItemAmount,
    rowNumber,
    maxRows,
    modalOpen,
    setModalOpen,
    retiredFeatureFlag,
    searchToggle,
  } = props;
  const intl = useIntl();
  const learnUrl = nameToLearnUrl(playlistItem.title);
  const url = `${learnUrl}?from=/playlist/${playlistUrl}`;
  const { accessState } = playlistItem;
  const { user } = useUserContext();
  const isLockedFlag = user?.hasLockedContentFlag;

  const { activeLanguage } = useLanguageContext();
  const [draggable, setDraggable] = React.useState(false);
  const [isRowHover, setIsRowHover] = React.useState(false);
  const actionsModal = useModal({ open: false });

  function scrollWithDrag(event: MouseEvent) {
    const height = window.innerHeight;
    const scrollThreshold = 100;
    const scrollLength = 5;
    const scrollPower = 5;

    const { clientY } = event;

    if (clientY < scrollThreshold) {
      // power allows the user to control the speed of scrolling by changing how far into the treshold they drag
      const power = Math.pow(scrollPower, 1 - clientY / scrollThreshold);
      window.scrollBy(0, -scrollLength * power);
    } else if (clientY > height - scrollThreshold) {
      // multiply scrollPower by 5 to compensate for less room for dragging on bottom of screen
      const power = Math.pow(
        scrollPower * 5,
        1 - scrollThreshold / (clientY - height + scrollThreshold),
      );
      window.scrollBy(0, scrollLength * power);
    }
  }

  let lockedSubProduct = '';
  if (accessState && Object.entries(accessState).length === 1) {
    for (const [key, value] of Object.entries(accessState)) {
      if (value === 'locked')
        lockedSubProduct = key.replace(/ /g, '_').toLowerCase();
    }
  }

  const displayTitle = getTranslatedTitle(playlistItem, activeLanguage);

  const toBeRetiredTag =
    retiredFeatureFlag &&
    playlistItem.data.tags?.some(
      (tags) => tags.tag?.toLowerCase() === 'to be retired',
    );

  const archivedStatus = !!playlistItem.data.archivedStatus;

  useEffect(() => {
    setModalOpen(actionsModal.isVisible);
  }, [actionsModal.isVisible, setModalOpen]);

  return (
    <Reorder.Item
      value={playlistItem}
      dragListener={isPlaylistMine && draggable}
      onDrag={(event: MouseEvent) => {
        scrollWithDrag(event);
      }}
      onDragEnd={() => setDraggable(false)}
      whileDrag={{
        boxShadow: '5px 5px 10px rgba(0,0,0,0.3)',
        position: 'relative',
      }}
      onFocus={() => setIsRowHover(true)}
      onBlur={() => setIsRowHover(false)}
    >
      <Flex
        onMouseEnter={() => {
          setIsRowHover(true);
        }}
        onMouseLeave={() => {
          setIsRowHover(false);
        }}
        data-testid="playlist-item-row"
        className={clsx(
          'p-4 justify-between rounded-16 transition-colors',
          isRowHover ? bg200 : bg0,
        )}
      >
        <Flex>
          <Flex
            className={clsx(
              'flex items-center',
              actionsModal.isVisible || (!modalOpen && isRowHover)
                ? 'opacity-100'
                : 'opacity-0',
            )}
          >
            <Box
              onMouseEnter={() => setDraggable(true)}
              onMouseLeave={() => setDraggable(false)}
              onTouchStart={() => setDraggable(true)}
              className="mx-2 opacity-[0.75] cursor-grab"
            >
              <IconButton
                type="button"
                data-testid="playlist.item.row.rearrange"
                tabIndex={-1}
                variant="transparent"
                icon={<Icon icon={faGripVertical} />}
                aria-label={intl.formatMessage({
                  id: 'playlist.item.row.rearrange',
                  defaultMessage: 'Rearrange',
                })}
                className="text-[20px]"
              />
            </Box>
          </Flex>
          {archivedStatus ? (
            <div
              className={clsx(
                'mr-4 relative w-[150px] h-[80px] min-w-[150px]',
                'cursor-default filter-grayscale',
              )}
              data-testid="thumbnail-container"
            >
              <ItemThumbnail
                thumbnailURL={`${PROD_MEDIA_SERVER}${playlistItem.data.thumbnail}`}
                title={playlistItem.title}
                playIcon={!playlistItem.data.notes}
                minHeight="80px"
                watchPercent={playlistItem.data.progress}
                duration={formatSecondsToDuration(
                  +(playlistItem?.data?.length || 0),
                )}
                toBeRetired={toBeRetiredTag}
                archivedStatus={!!archivedStatus}
              />
            </div>
          ) : (
            <a
              href={url}
              className={clsx(
                'mr-4 relative w-[150px] h-[80px] min-w-[150px]',
                'cursor-pointer',
              )}
              data-testid="thumbnail-container"
            >
              <ItemThumbnail
                thumbnailURL={`${PROD_MEDIA_SERVER}${playlistItem.data.thumbnail}`}
                title={playlistItem.title}
                playIcon={!playlistItem.data.notes}
                minHeight="80px"
                watchPercent={playlistItem.data.progress}
                duration={formatSecondsToDuration(
                  +(playlistItem?.data?.length || 0),
                )}
                toBeRetired={toBeRetiredTag}
                archivedStatus={!!archivedStatus}
              />
            </a>
          )}

          <Flex className="flex-1 w-[max-content] items-center">
            <Flex className="flex-col gap-4">
              <Flex className="items-center">
                <span>
                  <A
                    href={archivedStatus ? undefined : url}
                    className={clsx(
                      'opacity-[.75] font-bold no-underline',
                      archivedStatus
                        ? 'text-lightGray'
                        : isRowHover
                        ? textPrimary
                        : 'text-text',
                    )}
                  >
                    {displayTitle}
                  </A>
                  {lockedSubProduct && isLockedFlag ? (
                    <LockIcon variant={lockedSubProduct} />
                  ) : null}
                </span>
                {toBeRetiredTag && (
                  <Box className="ml-2">
                    <Tag
                      data-testid="playlist_retired_label"
                      cn={`text-sm font-bold px-2 py-1 rounded-lg cursor-pointer ${bgPrimaryLight}`}
                    >
                      {archivedStatus ? (
                        <FormattedMessage
                          defaultMessage="Retired"
                          id="playlist.item.retiredLabel"
                        />
                      ) : (
                        <FormattedMessage
                          defaultMessage="To be retired"
                          id="playlist.item.toBeRetiredLabel"
                        />
                      )}
                    </Tag>
                  </Box>
                )}
              </Flex>
              {toBeRetiredTag && (
                <Flex
                  onClick={() => {
                    localStorage.setItem(
                      'playlistItem',
                      JSON.stringify(playlistItem),
                    );
                    searchToggle();
                  }}
                  data-testid="playlist_replace_video_button"
                  className="items-center gap-1 cursor-pointer text-blue-700 dark:text-blue-100 font-bold"
                >
                  <Icon icon={faArrowsRotate} />
                  <FormattedMessage
                    defaultMessage="Replace this video"
                    id="playlist.item.replaceVideo"
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
        {(isPlaylistMine || (actionsModal.isVisible && !modalOpen)) && (
          <Flex
            data-testid="icons-container"
            className={`opacity-[0.75] ${textGray} items-center`}
          >
            <div className="absolute -translate-x-[200px]">
              <PopupMenu
                isPortal={false}
                keyExtractor={(item) => item.id}
                isVisible={actionsModal.isVisible}
                hide={actionsModal.hide}
                renderItem={(item) => <PopupMenuRow item={item} />}
                heading={intl.formatMessage({
                  id: 'playlist.item.row.actions',
                  defaultMessage: 'Actions',
                })}
                centerHeading={true}
                hideOnScroll={false}
                forceFixedPosition={true}
                items={[
                  {
                    disabled: !rowNumber,
                    id: '0',
                    text: intl.formatMessage({
                      id: 'playlist.item.row.moveUp',
                      defaultMessage: 'Move up',
                    }),
                    textColor: textGray780,
                    icon: faUpLong,
                    iconColor: textGray780,
                    onClick: () => moveItemAmount(playlistItem.data.nodeID, -1),
                    onKeyDown: (e) => {
                      if ([' ', 'Enter'].includes(e.key)) {
                        window.scrollBy({
                          top: -112,
                          behavior: 'instant',
                        });
                      }
                    },
                  },
                  {
                    disabled: rowNumber === maxRows - 1,
                    id: '1',
                    text: intl.formatMessage({
                      id: 'playlist.item.row.moveDown',
                      defaultMessage: 'Move down',
                    }),
                    textColor: textGray780,
                    icon: faDownLong,
                    iconColor: textGray780,
                    onClick: () => moveItemAmount(playlistItem.data.nodeID, 1),
                    onKeyDown: (e) => {
                      if ([' ', 'Enter'].includes(e.key)) {
                        window.scrollBy({
                          top: 112,
                          behavior: 'instant',
                        });
                      }
                    },
                  },
                  {
                    id: '2',
                    text: intl.formatMessage({
                      id: 'playlist.item.row.remove',
                      defaultMessage: 'Remove',
                    }),
                    textColor: textNegativeText,
                    icon: faTimes,
                    iconColor: textNegativeText,
                    onClick: () =>
                      onRemove([
                        {
                          learnNodeID: playlistItem.data.nodeID,
                          name: playlistItem.title,
                        },
                      ]),
                    onKeyDown: (e) => {
                      if (e.key === 'Tab' && !e.shiftKey) {
                        actionsModal.hide();
                      }
                    },
                  },
                ]}
              ></PopupMenu>
            </div>
            <Flex
              className={clsx(
                'transition-opacity',
                actionsModal.isVisible || (!modalOpen && isRowHover)
                  ? 'opacity-100'
                  : 'opacity-0',
              )}
            >
              <IconButton
                type="button"
                data-testid="playlist.item.row.toggle"
                icon={<Icon icon={faEllipsisV} />}
                onClick={() => {
                  actionsModal.toggle();
                  setModalOpen(true);
                }}
                variant="transparent"
                aria-label={intl.formatMessage({
                  id: 'playlist.item.row.actions',
                  defaultMessage: 'Actions',
                })}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Reorder.Item>
  );
};

export default PlaylistItemRow;
